import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import {
  approveLeaveReplacement,
  createLeaveReplacement,
  employeeLabelOf,
  listEmployees,
  listLeaveReplacementsByEmployee,
  type EmployeeRow,
  type LeaveReplacementRow,
} from '../../api/orgApi.js';
import { Field, SelectField, sx, todayIso } from './orgKit.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

export function LeaveReplacementPage(): ReactNode {
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [rows, setRows] = useState<LeaveReplacementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [replacementEmployeeId, setReplacementEmployeeId] = useState('');
  const [replacementStartDate, setReplacementStartDate] = useState(todayIso());
  const [replacementEndDate, setReplacementEndDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listEmployees(token).then((r) => setEmployees(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  const empName = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.empExtId, employeeLabelOf(e)])),
    [employees],
  );

  useEffect(() => {
    if (!employeeId) { setRows([]); return; }
    setLoading(true);
    listLeaveReplacementsByEmployee(token, employeeId)
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [employeeId, token]);

  function reload(): void {
    if (!employeeId) return;
    listLeaveReplacementsByEmployee(token, employeeId).then((r) => setRows(r.data)).catch((e) => setError((e as Error).message));
  }

  async function nominate(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (!employeeId) { setError(t('org.leave.selectFirst')); return; }
    if (!replacementEmployeeId) { setError(t('org.leave.selectReplacement')); return; }
    if (replacementEmployeeId === employeeId) { setError(t('org.leave.differentEmp')); return; }
    if (replacementEndDate < replacementStartDate) { setError(t('org.leave.dateOrder')); return; }
    setError(null);
    setBusy(true);
    try {
      await createLeaveReplacement(token, {
        employeeId,
        replacementEmployeeId,
        replacementStartDate,
        replacementEndDate,
        reason: reason.trim() || undefined,
      });
      setNotice(t('org.leave.nominated'));
      setShowAdd(false);
      setReplacementEmployeeId('');
      setReason('');
      reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function approve(id: string): Promise<void> {
    setBusyId(id);
    setError(null);
    try {
      await approveLeaveReplacement(token, id);
      setNotice(t('org.leave.approved'));
      reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  const employeeOptions = employees.map((e) => ({ value: e.empExtId, label: employeeLabelOf(e) }));
  const statusBg = (s?: string): string =>
    s === 'APPROVED' ? 'var(--color-success-bg)' : s === 'REJECTED' || s === 'CANCELLED' ? 'var(--color-danger-bg)' : 'var(--color-warning-bg)';

  return (
    <main aria-label="leave-replacement-list" style={sx.main}>
      <div style={sx.headerRow}>
        <h1>{t('org.leave.title')}</h1>
        {employeeId && (
          <Button type="button" variant="primary" onClick={() => { setShowAdd((v) => !v); setNotice(null); }}>
            {showAdd ? t('org.common.close') : t('org.leave.nominate')}
          </Button>
        )}
      </div>

      <div style={{ maxWidth: 460, marginBottom: 16 }}>
        <SelectField
          label={t('org.leave.employeeOnLeave')}
          name="employeeId"
          value={employeeId}
          onChange={(v) => { setEmployeeId(v); setShowAdd(false); setNotice(null); setError(null); }}
          options={employeeOptions}
          placeholder={t('org.leave.employeePh')}
        />
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}
      {notice && <p style={sx.ok}>{notice}</p>}

      {showAdd && (
        <form onSubmit={nominate} style={sx.card}>
          <SelectField label={t('org.leave.replacementEmployee')} name="replacementEmployeeId" value={replacementEmployeeId} onChange={setReplacementEmployeeId} options={employeeOptions} required />
          <Field label={t('org.field.startDate')} name="replacementStartDate" value={replacementStartDate} onChange={setReplacementStartDate} type="date" required />
          <Field label={t('org.field.endDate')} name="replacementEndDate" value={replacementEndDate} onChange={setReplacementEndDate} type="date" required />
          <Field label={t('org.field.reason')} name="reason" value={reason} onChange={setReason} placeholder={t('org.common.optional')} />
          <div style={sx.actionBar}>
            <Button type="submit" variant="primary" disabled={busy}>{busy ? t('org.kit.saving') : t('org.leave.nominateBtn')}</Button>
            <Button type="button" variant="secondary" onClick={() => setShowAdd(false)}>{t('common.cancel')}</Button>
          </div>
        </form>
      )}

      {!employeeId && <p>{t('org.leave.selectToView')}</p>}
      {employeeId && loading && <p>{t('common.loading')}</p>}
      {employeeId && !loading && rows.length === 0 && <p>{t('org.leave.empty')}</p>}
      {employeeId && rows.length > 0 && (
        <DataTable<LeaveReplacementRow>
          rowKey={(lr) => lr.leaveRepId}
          rows={rows}
          columns={[
            { key: 'replacement', header: t('org.col.replacement'), render: (lr) => empName[lr.replacementEmployeeId] ?? lr.replacementEmployeeId },
            { key: 'from', header: t('org.col.from'), render: (lr) => day(lr.replacementStartDate) },
            { key: 'to', header: t('org.col.to'), render: (lr) => day(lr.replacementEndDate) },
            { key: 'reason', header: t('org.col.reason'), render: (lr) => lr.reason ?? '—' },
            { key: 'status', header: t('org.col.status'), render: (lr) => <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: statusBg(lr.approvalStatus) }}>{lr.approvalStatus ?? 'PENDING'}</span> },
            { key: 'actions', header: t('org.col.actions'), render: (lr) => (lr.approvalStatus ?? 'PENDING') === 'PENDING'
              ? <Button type="button" variant="primary" disabled={busyId === lr.leaveRepId} onClick={() => approve(lr.leaveRepId)}>{t('org.leave.approve')}</Button>
              : <span style={{ color: 'var(--color-text-secondary)' }}>—</span> },
          ]}
        />
      )}
    </main>
  );
}
